import { useState } from 'react';
import { Checkbox } from '@/shared/components/Checkbox';
import {
  OutlinedDropdown,
  type OutlinedDropdownOption,
} from '@/shared/components/OutlinedDropdown';
import type { TechnicianPrivileges } from '../../types/addTechnicianType';
import { TECHNICIAN_LABELS } from '../../constants';

type PrivilegeKey = keyof TechnicianPrivileges;

function applyPrivilegeCascade(
  current: TechnicianPrivileges,
  key: PrivilegeKey,
  value: boolean
): TechnicianPrivileges {
  const next = { ...current, [key]: value };

  if (value) {
    if (key === 'taskUpdateStatus') {
      next.taskView = true;
    }
    if (key === 'taskFillForm') {
      next.taskView = true;
      next.taskUpdateStatus = true;
    }
    if (key === 'taskAcceptReject') {
      next.taskView = true;
    }
    if (key === 'certificateView') {
      next.certificateList = true;
    }
    if (key === 'certificateDownload') {
      next.certificateList = true;
      next.certificateView = true;
    }
    if (key === 'invoiceView') {
      next.invoiceList = true;
    }
    if (key === 'invoiceDownload') {
      next.invoiceList = true;
      next.invoiceView = true;
    }
  } else {
    if (key === 'taskView') {
      next.taskUpdateStatus = false;
      next.taskFillForm = false;
      next.taskAcceptReject = false;
    }
    if (key === 'taskUpdateStatus') {
      next.taskFillForm = false;
    }
    if (key === 'certificateList') {
      next.certificateView = false;
      next.certificateDownload = false;
    }
    if (key === 'certificateView') {
      next.certificateDownload = false;
    }
    if (key === 'invoiceList') {
      next.invoiceView = false;
      next.invoiceDownload = false;
    }
    if (key === 'invoiceView') {
      next.invoiceDownload = false;
    }
  }

  return next;
}

interface ModuleItem {
  key: PrivilegeKey | null;
  label: string;
  alwaysDisabled?: true;
}

interface ModuleConfig {
  id: string;
  title: string;
  items: ModuleItem[];
}

const MODULES: ModuleConfig[] = [
  {
    id: 'task',
    title: TECHNICIAN_LABELS.MODULE_MANAGE_TASKS,
    items: [
      {
        key: null,
        label: TECHNICIAN_LABELS.PRIV_TASK_LIST,
        alwaysDisabled: true,
      },
      { key: 'taskView', label: TECHNICIAN_LABELS.PRIV_TASK_VIEW },
      {
        key: 'taskUpdateStatus',
        label: TECHNICIAN_LABELS.PRIV_TASK_UPDATE_STATUS,
      },
      { key: 'taskFillForm', label: TECHNICIAN_LABELS.PRIV_TASK_FILL_FORM },
      {
        key: 'taskAcceptReject',
        label: TECHNICIAN_LABELS.PRIV_TASK_ACCEPT_REJECT,
      },
    ],
  },
  {
    id: 'certificate',
    title: TECHNICIAN_LABELS.MODULE_CERTIFICATES,
    items: [
      { key: 'certificateList', label: TECHNICIAN_LABELS.PRIV_CERT_LIST },
      { key: 'certificateView', label: TECHNICIAN_LABELS.PRIV_CERT_VIEW },
      {
        key: 'certificateDownload',
        label: TECHNICIAN_LABELS.PRIV_CERT_DOWNLOAD,
      },
    ],
  },
  {
    id: 'invoice',
    title: TECHNICIAN_LABELS.MODULE_INVOICES,
    items: [
      { key: 'invoiceList', label: TECHNICIAN_LABELS.PRIV_INVOICE_LIST },
      { key: 'invoiceView', label: TECHNICIAN_LABELS.PRIV_INVOICE_VIEW },
      {
        key: 'invoiceDownload',
        label: TECHNICIAN_LABELS.PRIV_INVOICE_DOWNLOAD,
      },
    ],
  },
];

function moduleToOptions(
  privileges: TechnicianPrivileges,
  module: ModuleConfig
): OutlinedDropdownOption[] {
  return module.items.map((item) => {
    const option: OutlinedDropdownOption = {
      value: item.key ?? `${module.id}-always`,
      label: item.label,
      checked: item.key === null ? true : privileges[item.key],
    };
    if (item.alwaysDisabled) {
      option.disabled = true;
    }
    return option;
  });
}

function getAllState(privileges: TechnicianPrivileges): {
  checked: boolean;
  indeterminate: boolean;
} {
  const allKeys = Object.keys(privileges) as PrivilegeKey[];
  const checkedCount = allKeys.filter((k) => privileges[k]).length;
  // One always-checked item ("List of Assigned Tasks") always contributes to the total,
  // so Select All is indeterminate whenever any real privilege is unchecked.
  return {
    checked: checkedCount === allKeys.length,
    indeterminate: checkedCount < allKeys.length,
  };
}

interface PrivilegesSectionProps {
  privileges: TechnicianPrivileges;
  onPrivilegesChange: (privileges: TechnicianPrivileges) => void;
  disabled?: boolean;
}

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className={`transition-transform duration-200 shrink-0 ${open ? '' : '-rotate-90'}`}
  >
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="#474A4C"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PrivilegesSection = ({
  privileges,
  onPrivilegesChange,
  disabled = false,
}: PrivilegesSectionProps) => {
  const [isSectionOpen, setIsSectionOpen] = useState(true);

  const handleModuleChange = (
    module: ModuleConfig,
    newOptions: OutlinedDropdownOption[]
  ) => {
    const prevOptions = moduleToOptions(privileges, module);
    let next = { ...privileges };
    newOptions.forEach((newOpt, i) => {
      const prevOpt = prevOptions[i];
      if (prevOpt && newOpt.checked !== prevOpt.checked) {
        const key = module.items[i]?.key;
        if (key !== null && key !== undefined) {
          next = applyPrivilegeCascade(next, key, newOpt.checked ?? false);
        }
      }
    });
    onPrivilegesChange(next);
  };

  const handleSelectAll = (value: boolean) => {
    const next = { ...privileges };
    (Object.keys(next) as PrivilegeKey[]).forEach((k) => {
      next[k] = value;
    });
    onPrivilegesChange(next);
  };

  const allState = getAllState(privileges);

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setIsSectionOpen((prev) => !prev)}
        className="flex items-center justify-between w-full text-left cursor-pointer"
      >
        <p className="text-[18px] text-on-surface-variant">
          {TECHNICIAN_LABELS.PRIVILEGES_TITLE}
        </p>
        <ChevronIcon open={isSectionOpen} />
      </button>

      {isSectionOpen && (
        <div className="flex flex-col gap-4">
          <Checkbox
            label={TECHNICIAN_LABELS.PRIVILEGES_SELECT_ALL}
            checked={allState.checked}
            indeterminate={allState.indeterminate}
            onChange={handleSelectAll}
            disabled={disabled}
            testId="privileges-select-all"
          />

          <div className="flex flex-wrap gap-4">
            {MODULES.map((module) => (
              <div
                key={module.id}
                className="w-full sm:w-[calc(50%-8px)] lg:flex-1"
              >
                <OutlinedDropdown
                  title={module.title}
                  options={moduleToOptions(privileges, module)}
                  onChange={(updated) => handleModuleChange(module, updated)}
                  disabled={disabled}
                  testId={module.id}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivilegesSection;
